import { mapActions, mapGetters } from 'vuex'
import ChartComponent from '@/components/Chart/Chart.vue'

export default {
  name: 'AdminImpactReport',
  components: {
    ChartComponent
  },
  data () {
    return {
      isLoading: {
        report: false
      },
      selectedWeeks: 26,
      weekOptions: [
        { text: '12w', value: 12 },
        { text: '26w', value: 26 },
        { text: '52w', value: 52 }
      ],
      search: '',
      report: {
        summary: {},
        latestSnapshot: null,
        history: [],
        contentplans: []
      },
      chartVersion: 0,
      trafficChartData: null,
      trafficChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: tooltipItem => `Avg clicks: ${Math.round(tooltipItem.yLabel).toLocaleString('en-US')}`
          }
        },
        animation: {
          duration: 220,
          easing: 'easeOutQuart'
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontColor: '#687178',
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontColor: '#687178',
              callback: value => this.formatCompact(value)
            },
            gridLines: {
              color: 'rgba(23, 32, 38, 0.07)',
              zeroLineColor: 'rgba(23, 32, 38, 0.1)'
            }
          }]
        }
      },
      tableHeaders: [
        { text: 'Contentplan', value: 'contentplanName', width: '300px' },
        { text: 'Traffic', value: 'clicks', align: 'right' },
        { text: 'Change', value: 'clickChange', sortable: false },
        { text: 'Published', value: 'publishedPages', align: 'right' },
        { text: 'Active pages', value: 'activePages', align: 'right' },
        { text: 'Impressions', value: 'impressions', align: 'right' },
        { text: 'Trend', value: 'history', sortable: false },
        { text: 'Status', value: 'status', sortable: false, width: '180px' }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'user'
    ]),
    isAdmin () {
      return !!this.user?.admin
    },
    summary () {
      return this.report.summary || {}
    },
    latestSnapshot () {
      return this.report.latestSnapshot || null
    },
    rows () {
      return Array.isArray(this.report.contentplans) ? this.report.contentplans : []
    },
    failedRows () {
      return this.rows.filter(row => row.status === 'failed')
    },
    visibleRows () {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.rows
      return this.rows.filter(row => [
        row.contentplanName,
        row.workspaceName,
        row.domain,
        row.gscProperties
      ].some(value => `${value || ''}`.toLowerCase().includes(query)))
    }
  },
  methods: {
    ...mapActions([
      'adminImpactReportGet'
    ]),
    async loadData () {
      if (!this.isAdmin) return

      this.isLoading.report = true
      try {
        const report = await this.adminImpactReportGet({ weeks: this.selectedWeeks })
        this.report = {
          summary: report?.summary || {},
          latestSnapshot: report?.latestSnapshot || null,
          history: Array.isArray(report?.history) ? report.history : [],
          contentplans: Array.isArray(report?.contentplans) ? report.contentplans : []
        }
        this.buildTrafficChart()
      } catch (err) {
        this.$store.commit('setSnackbar', {
          open: true,
          text: err?.response?.data?.message || err.message || 'Failed to load impact report',
          type: 'error'
        })
      }
      this.isLoading.report = false
    },
    buildTrafficChart () {
      const history = this.report.history || []
      if (!history.length) {
        this.trafficChartData = null
        this.chartVersion++
        return
      }

      this.trafficChartData = {
        labels: history.map(row => this.formatShortDate(row.periodEndDate)),
        datasets: [{
          label: 'Average 30-day clicks',
          data: history.map(row => Math.round(row.avgClicksPerContentplan || 0)),
          borderColor: '#245b7c',
          backgroundColor: 'rgba(36, 91, 124, 0.08)',
          pointBackgroundColor: '#245b7c',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 3,
          lineTension: 0.25,
          borderWidth: 2,
          fill: true
        }]
      }
      this.chartVersion++
    },
    formatNumber (value) {
      const numeric = Number(value) || 0
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: numeric >= 100 ? 0 : 1
      }).format(numeric)
    },
    formatCompact (value) {
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(Number(value) || 0)
    },
    formatSignedNumber (value) {
      const numeric = Number(value) || 0
      const prefix = numeric > 0 ? '+' : ''
      return `${prefix}${this.formatNumber(numeric)}`
    },
    formatSignedPercent (value) {
      const numeric = Number(value) || 0
      const prefix = numeric > 0 ? '+' : ''
      return `${prefix}${numeric.toFixed(1)}%`
    },
    formatCoverage (summary) {
      const measured = Number(summary.measuredContentplanCount) || 0
      const total = Number(summary.totalContentplanCount) || 0
      if (!total) return '0%'
      return `${Math.round((measured / total) * 100)}%`
    },
    formatShortDate (value) {
      if (!value) return ''
      const date = new Date(`${value}T00:00:00`)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    },
    trendClass (value) {
      const numeric = Number(value) || 0
      if (numeric > 0) return 'admin-impact-trend admin-impact-trend--up'
      if (numeric < 0) return 'admin-impact-trend admin-impact-trend--down'
      return 'admin-impact-trend admin-impact-trend--flat'
    },
    trendIcon (value) {
      const numeric = Number(value) || 0
      if (numeric > 0) return 'mdi-arrow-up'
      if (numeric < 0) return 'mdi-arrow-down'
      return 'mdi-minus'
    },
    trendIconColor (value) {
      const numeric = Number(value) || 0
      if (numeric > 0) return '#1f7a4d'
      if (numeric < 0) return '#b63f3f'
      return '#687178'
    },
    statusClass (status) {
      return status === 'failed'
        ? 'admin-impact-status admin-impact-status--failed'
        : 'admin-impact-status admin-impact-status--success'
    },
    miniBars (item) {
      const history = Array.isArray(item.history) ? item.history.slice(-12) : []
      const values = history.map(row => Number(row.clicks) || 0)
      const max = Math.max(...values, 1)

      return history.map(row => {
        const value = Number(row.clicks) || 0
        return {
          height: Math.max(4, Math.round((value / max) * 28)),
          opacity: row.status === 'failed' ? 0.25 : 1
        }
      })
    }
  },
  async mounted () {
    document.title = 'Impact Report | ContentGecko'
    await this.loadData()
  }
}
